import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Wifi, WifiOff, MessageSquare, RefreshCw, MoreHorizontal,
  Smartphone, ThermometerSun, Infinity,
} from "lucide-react";
import { useInstances } from "@/hooks/useInstances";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FlagImage, defaultCountries, guessCountryByPartialPhoneNumber } from "react-international-phone";
// Effective days calculation based on accumulated seconds + live session
function calculateEffectiveDays(inst: {
  total_active_seconds: number | null;
  profile_active_seconds: number | null;
  last_connected_at: string | null;
  evolution_connection_state: string | null;
}) {
  const isOnline = inst.evolution_connection_state === "open";
  const liveSeconds =
    isOnline && inst.last_connected_at
      ? (Date.now() - new Date(inst.last_connected_at).getTime()) / 1000
      : 0;
  const totalEffectiveDays = Math.floor(((inst.total_active_seconds || 0) + liveSeconds) / 86400);
  const phaseDays = Math.floor(((inst.profile_active_seconds || 0) + liveSeconds) / 86400);
  return { totalEffectiveDays, phaseDays };
}

interface DashboardSummary {
  ports: { total: number; used: number };
  status: { online: number; offline: number; mature: number };
  today_messages: { sent: number; received: number };
}

export default function Dashboard() {
  const [tab, setTab] = useState("all");
  const { user } = useAuth();
  const { instances, isLoading: instancesLoading } = useInstances();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const summaryQuery = useQuery({
    queryKey: ["dashboard_summary", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_dashboard_summary", { p_user_id: user!.id });
      if (error) throw error;
      return data as unknown as DashboardSummary;
    },
    enabled: !!user?.id,
  });

  // Fetch total messages sent per instance (all time)
  const { data: totalMessagesByInstance = {} } = useQuery({
    queryKey: ["total_messages_by_instance", user?.id],
    queryFn: async () => {
      const instIds = instances.map(i => i.id);
      if (instIds.length === 0) return {};
      const { data, error } = await supabase
        .from("daily_metrics")
        .select("instance_id, messages_sent")
        .in("instance_id", instIds);
      if (error) throw error;
      const map: Record<string, number> = {};
      for (const row of data || []) {
        map[row.instance_id] = (map[row.instance_id] || 0) + (row.messages_sent || 0);
      }
      return map;
    },
    enabled: !!user?.id && instances.length > 0,
  });

  const summary = summaryQuery.data;
  const isLoading = instancesLoading || summaryQuery.isLoading;

  const filtered = tab === "all"
    ? instances
    : tab === "online"
      ? instances.filter((i) => i.evolution_connection_state === "open")
      : instances.filter((i) => i.evolution_connection_state !== "open");

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = () => {
    setRefreshing(true);
    queryClient.invalidateQueries({ queryKey: ["instances"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard_summary"] });
    setTimeout(() => setRefreshing(false), 1000);
  };

  const statCards = [
    {
      title: "Contas Online",
      value: summary ? String(summary.status.online) : "—",
      subtitle: summary ? `de ${summary.ports.used} portas em uso` : "carregando...",
      icon: Wifi,
      accent: "text-accent",
    },
    {
      title: "Mensagens Hoje",
      value: summary ? String(summary.today_messages.sent + summary.today_messages.received) : "—",
      subtitle: summary ? `${summary.today_messages.sent} enviadas · ${summary.today_messages.received} recebidas` : "carregando...",
      icon: MessageSquare,
      accent: "text-primary",
    },
    {
      title: "Contas Offline",
      value: summary ? String(summary.status.offline) : "—",
      subtitle: "hoje",
      icon: WifiOff,
      accent: "text-destructive",
    },
    {
      title: "Contas Maduras",
      value: summary ? String(summary.status.mature) : "—",
      subtitle: "nas fases Quente ou Manutenção",
      icon: ThermometerSun,
      accent: "text-yellow-400",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Visão Geral</h1>
          <p className="text-muted-foreground text-sm mt-1">Gerencie seus números e acompanhe o aquecimento.</p>
        </div>
        <Button size="sm" variant="outline" onClick={handleRefresh} className="gap-2">
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, i) => (
          <motion.div key={card.title} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium text-muted-foreground">{card.title}</CardTitle>
                <card.icon className={`h-5 w-5 ${card.accent}`} />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <>
                    <Skeleton className="h-8 w-16 mb-1" />
                    <Skeleton className="h-3 w-28" />
                  </>
                ) : (
                  <>
                    <div className="font-display text-2xl font-bold text-foreground">{card.value}</div>
                    <p className="text-xs text-muted-foreground mt-1">{card.subtitle}</p>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Table or Empty State */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
        <Card className="glass-card border-warmz-glass-border/50">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg font-display">Números Conectados</CardTitle>
              {instances.length > 0 && (
                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList className="bg-secondary/60">
                    <TabsTrigger value="all">Todos</TabsTrigger>
                    <TabsTrigger value="online">Online</TabsTrigger>
                    <TabsTrigger value="offline">Offline</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map((n) => (
                  <Skeleton key={n} className="h-12 w-full" />
                ))}
              </div>
            ) : instances.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <div className="rounded-full bg-muted p-4 mb-4">
                  <Smartphone className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Nenhum número conectado ainda</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                  Conecte seu primeiro número para começar o aquecimento automático e proteger suas contas.
                </p>
                <Button onClick={() => navigate("/dashboard/numbers")} className="gap-2">
                  <Smartphone className="h-4 w-4" />
                  Conectar Número
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                      <TableHead>Conta WA</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Dias Online</TableHead>
                      <TableHead className="hidden md:table-cell">Total Enviadas</TableHead>
                      <TableHead>Aquecimento</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((inst, i) => {
                      const isOnline = inst.evolution_connection_state === "open";
                      const profile = inst.warmup_profiles;
                      const { totalEffectiveDays, phaseDays } = calculateEffectiveDays(inst);
                      const durationDays = profile?.duration_days;
                      const progressPct = durationDays
                        ? Math.min(100, Math.round((phaseDays / durationDays) * 100))
                        : 100;
                      const isInfinite = !durationDays;
                      const profileIcon = profile?.ui_icon || "❄️";
                      const profileName = profile?.name?.split(" / ")[0] || "Frio";

                      return (
                        <motion.tr
                          key={inst.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 + i * 0.05 }}
                          className="border-b border-warmz-glass-border/20 hover:bg-warmz-card-hover/40 transition-colors"
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <FlagImage iso2={guessCountryByPartialPhoneNumber({ phone: "+" + inst.phone_number.replace(/\D/g, ""), countries: defaultCountries }).country?.iso2 || "br"} size="18px" />
                              <div className="flex flex-col">
                                <span className="font-mono text-sm">{inst.phone_number}</span>
                                {inst.nickname && (
                                  <span className="text-[11px] text-muted-foreground leading-tight">{inst.nickname}</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs border-warmz-glass-border/50">
                              {inst.account_type === "Business" ? "Business" : "Pessoal"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <div className={`w-2 h-2 rounded-full ${isOnline ? "bg-accent animate-pulse" : "bg-destructive"}`} />
                              <span className={`text-xs font-medium ${isOnline ? "text-accent" : "text-destructive"}`}>
                                {isOnline ? "Online" : "Offline"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <span className="text-sm text-foreground font-medium">{totalEffectiveDays} dias (efetivos)</span>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <span className="text-sm font-mono font-medium text-foreground">
                              {(totalMessagesByInstance[inst.id] || 0).toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="min-w-[200px] space-y-1">
                              <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                                <span>{profileIcon}</span>
                                <span>{profileName}</span>
                                {isInfinite ? (
                                  <span className="flex items-center gap-0.5 text-muted-foreground ml-1">
                                    <Infinity className="h-3 w-3" />
                                    <span>Operação Contínua</span>
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground ml-1">
                                    ({phaseDays}/{durationDays} dias)
                                  </span>
                                )}
                              </div>
                              {isInfinite ? (
                                <div className="h-2 rounded-full bg-gradient-to-r from-accent to-yellow-400 w-full" />
                              ) : (
                                <Progress value={progressPct} className="h-2" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/dashboard/numbers")}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </motion.tr>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
